import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plane, Mail, MapPin, Package, User, Tag, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import AirCotizacionesProveedoresSection from '@/pages/air-requests/cotizaciones/components/AirCotizacionesProveedoresSection.jsx';

const ESTADO_BADGE = {
  Borrador: 'bg-gray-100 text-gray-700 border-gray-200',
  Enviada: 'bg-blue-100 text-blue-700 border-blue-200',
  Aprobada: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Rechazada: 'bg-rose-100 text-rose-700 border-rose-200',
};

const shortId = (id) => (id ? String(id).slice(-6) : '—');

const formatDate = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch {
    return '—';
  }
};

const formatCliente = (r) => r?.clienteSnapshot?.nombre || shortId(r?.clienteId);
const formatClienteNit = (r) => r?.clienteSnapshot?.nit || '';

/**
 * Detalle de una solicitud aérea.
 *
 * Las cotizaciones de proveedor viven en su propia sección, que maneja
 * sus diálogos (cargar, editar, comparar): este componente hace
 * `return null` antes de tener datos y no puede alojar ese estado.
 */
export default function AirRequestDetailDialog({ request, open, onOpenChange, onChanged }) {
  if (!request) return null;
  const r = request;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[80vw] max-w-none max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-6 py-4 border-b bg-muted/30 flex-shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 p-2 rounded-lg">
                <Plane className="h-5 w-5 text-primary" />
              </div>
              <div className="space-y-0.5">
                <DialogTitle className="text-lg">
                  {r.codigo || shortId(r._id)}
                </DialogTitle>
                <DialogDescription className="text-xs">
                  Creada el {formatDate(r.createdAt)} · {r.tipoOperacion}
                </DialogDescription>
              </div>
            </div>
            <Badge
              variant="outline"
              className={cn('font-normal border', ESTADO_BADGE[r.estado] || '')}
            >
              {r.estado}
            </Badge>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5">
          <DetailSection icon={User} label="Cliente y asesor">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DetailKV
                label="Cliente"
                value={formatCliente(r)}
                sub={formatClienteNit(r) ? `NIT ${formatClienteNit(r)}` : null}
              />
              <DetailKV
                label="Asesor comercial"
                value={
                  r.asesorSnapshot?.nombre ||
                  (r.asesorId ? shortId(r.asesorId) : 'No asignado')
                }
                sub={r.asesorSnapshot?.correo || null}
                icon={Mail}
              />
              <DetailKV
                label="Incoterm"
                value={
                  r.incotermSnapshot
                    ? `${r.incotermSnapshot.codigo} — ${r.incotermSnapshot.nombre}`
                    : shortId(r.incotermId)
                }
                sub={r.incotermSnapshot?.version || null}
                icon={Tag}
              />
              <DetailKV
                label="Cliente (correo / teléfono)"
                value={r.clienteSnapshot?.correo || '—'}
                sub={r.clienteSnapshot?.telefono || null}
                icon={Mail}
              />
              {r.valorMercancia != null ? (
                <DetailKV
                  label="Valor de la mercancía"
                  value={`${Number(r.valorMercancia).toLocaleString('es-CO')} ${
                    r.monedaValorMercancia || 'USD'
                  }`}
                  sub="Declarado por el cliente (aproximado)"
                  icon={Tag}
                />
              ) : null}
            </div>
          </DetailSection>

          <DetailSection icon={MapPin} label="Ruta aérea">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <AirportCard
                title="Aeropuerto de carga (origen)"
                snap={r.aeropuertoCargaSnapshot}
                pais={r.paisOrigen}
                fallbackId={r.aeropuertoCargaId}
              />
              <AirportCard
                title="Aeropuerto de descarga (destino)"
                snap={r.aeropuertoDescargaSnapshot}
                pais={r.paisDestino}
                fallbackId={r.aeropuertoDescargaId}
              />
            </div>
          </DetailSection>

          <DetailSection icon={Package} label="Carga">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="No. Piezas" value={r.numPiezas} />
              <Stat label="Peso total" value={`${Number(r.pesoTotal || 0).toFixed(2)} kg`} />
              <Stat label="Peso volumétrico" value={`${Number(r.pesoVolumetrico || 0).toFixed(2)} kg`} />
              <Stat label="Items" value={r.detalles?.length || 0} />
            </div>
          </DetailSection>

          {r.descripcionResumen ? (
            <DetailSection icon={FileText} label="Descripción">
              <p className="text-sm text-foreground whitespace-pre-wrap">
                {r.descripcionResumen}
              </p>
            </DetailSection>
          ) : null}

          {Array.isArray(r.detalles) && r.detalles.length > 0 ? (
            <DetailSection icon={Package} label="Detalle de paquetes">
              <div className="rounded-md border overflow-hidden">
                <div className="hidden sm:grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1.2fr] gap-1 px-3 py-2 bg-muted/30 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                  <span>Piezas</span>
                  <span>Largo</span>
                  <span>Ancho</span>
                  <span>Alto</span>
                  <span>Peso</span>
                  <span>Tipo / Apilable</span>
                </div>
                <div className="divide-y">
                  {r.detalles.map((d, i) => (
                    <div
                      key={d._id || i}
                      className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_1fr_1fr_1fr_1.2fr] gap-1 px-3 py-2 text-sm hover:bg-muted/10"
                    >
                      <span className="font-mono">{d.numPiezas ?? '—'}</span>
                      <span className="font-mono">
                        {d.largo ?? '—'} {d.unidadDimension || ''}
                      </span>
                      <span className="font-mono">
                        {d.ancho ?? '—'} {d.unidadDimension || ''}
                      </span>
                      <span className="font-mono">
                        {d.alto ?? '—'} {d.unidadDimension || ''}
                      </span>
                      <span className="font-mono">
                        {d.peso ?? '—'} {d.unidadPeso || ''}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {d.tipoMercanciaSnapshot?.nombre ||
                          d.tipoMercanciaSnapshot?.descripcion ||
                          'Sin tipo'}{' '}
                        · {d.apilable || '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </DetailSection>
          ) : null}

          {/* ============ Cotizaciones de Proveedores ============ */}
          <AirCotizacionesProveedoresSection
            airRequestId={r._id}
            airRequest={r}
            onChanged={onChanged}
          />

          {/* La trazabilidad NO se monta acá: tiene su propio modal,
              que se abre con la acción "Trazabilidad" de la tarjeta o
              del tablero. Este diálogo muestra solo el detalle de la
              operación. */}

          <div className="text-[10px] text-muted-foreground font-mono pt-2 border-t">
            <div>ID interno: {r._id}</div>
            {r.clienteId ? <div>clienteId: {r.clienteId}</div> : null}
            {r.asesorId ? <div>asesorId: {r.asesorId}</div> : null}
          </div>
        </div>
      </DialogContent>

    </Dialog>
  );
}

function DetailSection({ icon: Icon, label, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center justify-center w-6 h-6 rounded-md bg-primary/10 text-primary">
          <Icon size={13} />
        </div>
        <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {label}
        </span>
        <div className="flex-1 h-px bg-border" />
      </div>
      {children}
    </section>
  );
}

function DetailKV({ label, value, sub, icon: Icon }) {
  return (
    <div className="space-y-0.5">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        {Icon ? <Icon className="h-3 w-3" /> : null}
        {label}
      </div>
      <div className="text-sm text-foreground break-words">{value || '—'}</div>
      {sub ? <div className="text-xs text-muted-foreground">{sub}</div> : null}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border bg-muted/20 px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-base font-semibold text-foreground mt-0.5">
        {value}
      </div>
    </div>
  );
}

function AirportCard({ title, snap, pais, fallbackId }) {
  return (
    <div className="rounded-md border bg-muted/20 p-3 space-y-1">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        <MapPin className="h-3 w-3" />
        {title}
      </div>
      {snap?.nombre ? (
        <>
          <div className="text-sm font-medium text-foreground">
            {snap.nombre}{' '}
            {snap.iata ? (
              <span className="text-xs font-mono text-muted-foreground">
                ({snap.iata})
              </span>
            ) : null}
          </div>
          <div className="text-xs text-muted-foreground">
            {[snap.ciudad, snap.pais].filter(Boolean).join(', ')}
          </div>
        </>
      ) : (
        <>
          <div className="text-sm font-medium text-foreground">
            {pais || shortId(fallbackId)}
          </div>
          <div className="text-[10px] font-mono text-muted-foreground">
            {fallbackId}
          </div>
        </>
      )}
    </div>
  );
}
